/**
 * 리포트 요청 추적 훅
 * 
 * 종목 상세 페이지에서 리포트가 없을 때 요청하는 기능
 * - start()는 세션 스토리지를 확인하여 저장된 의도가 있으면 자동 복원
 * - 로그인 후 리다이렉트되어도 같은 의도 유지
 * - 로그인 의도는 자동으로 부모 의도 연결 (parentIntentType)
 * 
 * @example
 * ```tsx
 * function StockDetailPage({ ticker, market }) {
 *   const reportRequest = useReportRequest({ ticker, market });
 *   const loginIntent = useLoginIntent();
 *   const isLoggedIn = useAuth().isLoggedIn;
 *   
 *   const handleRequest = async () => {
 *     reportRequest.start(); // 의도 시작
 *     
 *     if (!isLoggedIn) {
 *       // 로그인 의도 시작 (부모 의도 자동 연결)
 *       loginIntent.start(
 *         { redirectUrl: router.asPath },
 *         { parentIntentType: 'report_request' }  // 👈 타입으로 부모 의도 자동 조회
 *       );
 *       router.push('/login');
 *       return;
 *     }
 *     
 *     try {
 *       await apiRequestReport(ticker, market);
 *       reportRequest.success();
 *     } catch (error) {
 *       reportRequest.fail('backend_5xx');
 *     }
 *   };
 *   
 *   return <button onClick={handleRequest}>리포트 요청</button>;
 * }
 * ```
 */

import { useCallback } from 'react';
import { track } from '../../../../core/client';
import { useIntent } from '../useIntent';

export interface ReportRequestContext {
    ticker: string;
    market: 'kr' | 'us';
}

export interface ReportRequestActions {
    start: () => void;
    success: () => void;
    fail: (reason?: string) => void;
}

/**
 * 리포트 요청 추적 훅
 */
export function useReportRequest(
    context: ReportRequestContext
): ReportRequestActions {
    const { ticker, market } = context;

    const intent = useIntent<ReportRequestContext>({
        type: 'report_request',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            track('report_request_start', {
                intent_id,
                ticker: context?.ticker || ticker, // start 실행 input | useReportRequest 정의시 input
                market: context?.market || market,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            track('report_request_success', {
                intent_id,
                ticker: context?.ticker || ticker,
                market: context?.market || market,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            track('report_request_fail', {
                intent_id,
                ticker: context?.ticker || ticker,
                market: context?.market || market,
                fail_reason,
            });
        },
    });

    /**
     * 리포트 요청 시작
     */
    const start = useCallback(() => {
        intent.start(context);
    }, [intent, context]);

    /**
     * 리포트 요청 성공
     */
    const success = useCallback(() => {
        intent.success();
    }, [intent]);

    /**
     * 리포트 요청 실패
     */
    const fail = useCallback((reason?: string) => {
        intent.fail(reason);
    }, [intent]);

    return {
        start,
        success,
        fail,
    };
}

